export type Period = "day" | "week";
export type PeriodGroup<T> = { key: string; label: string; rows: T[] };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Calendar parts of a moment in UTC or in the browser's time zone. */
function parts(ms: number, utc: boolean) {
  const d = new Date(ms);
  return utc
    ? { y: d.getUTCFullYear(), m: d.getUTCMonth(), d: d.getUTCDate(), dow: d.getUTCDay() }
    : { y: d.getFullYear(), m: d.getMonth(), d: d.getDate(), dow: d.getDay() };
}

/** Day number (days since 1970) of a calendar date, so days can be compared and shifted. */
const dayNo = (p: { y: number; m: number; d: number }) => Math.floor(Date.UTC(p.y, p.m, p.d) / 86_400_000);
const fromDayNo = (no: number) => {
  const d = new Date(no * 86_400_000);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth(), d: d.getUTCDate() };
};
const short = (p: { y: number; m: number; d: number }, withYear: boolean) =>
  `${p.d} ${MONTHS[p.m]}${withYear ? ` ${p.y}` : ""}`;

/**
 * Split rows (already newest first) into days or weeks (Monday to Sunday), in
 * UTC or local time. Rows without a usable time go to a last "Unknown" group.
 */
export function groupByPeriod<T>(
  rows: T[],
  timeOf: (row: T) => string | null | undefined,
  period: Period,
  utc: boolean,
  now = Date.now()
): PeriodGroup<T>[] {
  const today = dayNo(parts(now, utc));
  const thisYear = parts(now, utc).y;
  const groups: PeriodGroup<T>[] = [];
  const index = new Map<string, PeriodGroup<T>>();

  for (const row of rows) {
    const ms = Date.parse(timeOf(row) || "");
    let key = "unknown";
    let label = "Unknown date";
    if (Number.isFinite(ms)) {
      const p = parts(ms, utc);
      const no = dayNo(p);
      if (period === "day") {
        key = `d${no}`;
        label = no === today ? `Today · ${short(p, false)}` : no === today - 1 ? `Yesterday · ${short(p, false)}` : short(p, p.y !== thisYear);
      } else {
        const monday = no - ((p.dow + 6) % 7);
        const from = fromDayNo(monday);
        const to = fromDayNo(monday + 6);
        key = `w${monday}`;
        const range = `${short(from, false)} – ${short(to, to.y !== thisYear)}`;
        const thisMonday = today - ((parts(now, utc).dow + 6) % 7);
        label = monday === thisMonday ? `This week · ${range}` : monday === thisMonday - 7 ? `Last week · ${range}` : range;
      }
    }
    let group = index.get(key);
    if (!group) {
      group = { key, label, rows: [] };
      index.set(key, group);
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
}
